import { For, type JSX, Show } from 'solid-js';
import { t, tPlural } from '../i18n';
import type { AttentionPartKind, FleetTiles } from '../lib/fleet-attention';
import type { MachineFilter } from '../lib/fleet-machines';
import { HELD_REASON_KEY, type QueueLine, queueLine } from '../lib/fleet-queue-line';
import { StatValue } from './StatValue';

// The Fleet page's four health tiles (#6559, prototype `tiles()`): Online, Busy right
// now, Waiting in line, Needs attention. Presenter over `fleetTiles`
// (`lib/fleet-attention.ts`), which counts the server's machine verdicts.
//
// THREE ARE FILTERS over the Machines list (#6560): Online shows all, Busy right now the
// working ones, Needs attention its chip, each a toggle button pressed while its filter
// is the list's (`?filter=`). Waiting in line counts work, not machines, so it filters
// nothing and stays a plain tile.
//
// A value reads "-" while the utilization read is in flight and "Unavailable" when it
// failed with nothing cached (`StatValue`, #298). Healthy is neutral, never green; the
// only hue here is the Needs attention count, in the warning text colour.

const PART_KEY: Readonly<Record<AttentionPartKind, string>> = {
  overloaded: 'fleet.top.tiles.part_overloaded',
  not_responding: 'fleet.top.tiles.not_responding',
  low_disk: 'fleet.top.tiles.part_low_disk',
  memory: 'fleet.top.tiles.part_memory',
  stuck_job: 'fleet.top.tiles.part_stuck_job',
  cleanup: 'fleet.top.tiles.part_cleanup',
};

function onlineSub(o: FleetTiles['online'], starting: number): string {
  if (o.notResponding > 0) return t('fleet.top.tiles.not_responding', { count: o.notResponding });
  if (o.waiting > 0) return t('fleet.top.tiles.waiting_to_connect', { count: o.waiting });
  if (o.online === o.total) return t('fleet.top.tiles.online_all');
  // Not yet checked in, inside its first-beat grace: starting, not silent.
  return starting > 0
    ? t('fleet.top.tiles.starting', { count: starting })
    : t('fleet.top.tiles.unread');
}

function busySub(b: FleetTiles['busy']): string {
  if (b.total > 0 && b.working === b.total) return t('fleet.top.tiles.busy_all');
  const parts = [
    b.idle > 0 ? t('fleet.top.tiles.idle', { count: b.idle }) : '',
    b.paused > 0 ? t('fleet.top.tiles.paused', { count: b.paused }) : '',
  ].filter((p) => p !== '');
  const [first, second] = parts;
  if (first !== undefined && second !== undefined) {
    return t('fleet.top.tiles.pair', { first, second });
  }
  if (first !== undefined) return first;
  if (b.notResponding > 0) return t('fleet.top.tiles.not_responding', { count: b.notResponding });
  return b.starting > 0 ? t('fleet.top.tiles.starting', { count: b.starting }) : '';
}

function attentionSub(parts: FleetTiles['attention']['parts']): string {
  const words = parts.map((p) => t(PART_KEY[p.kind], { count: p.count }));
  const [first = '', second] = words;
  if (second === undefined) return first;
  return t(words.length > 2 ? 'fleet.top.tiles.pair_more' : 'fleet.top.tiles.pair', {
    first,
    second,
  });
}

/** The Waiting in line sub-line (#6708): the gate `queueLine` names, in words. */
function queueSub(line: QueueLine): string {
  switch (line.kind) {
    case 'machine_free':
      return t('fleet.top.tiles.queue_waiting');
    case 'empty':
      return t('fleet.top.tiles.queue_empty');
    case 'dispatching':
      return t('fleet.top.tiles.dispatching');
    case 'key_room':
      return tPlural('fleet.top.tiles.held_key_room', line.n, { count: line.n });
    case 'reserved':
      return t('fleet.top.tiles.held_reserved', { count: line.n });
    case 'area_busy':
      return tPlural('fleet.top.tiles.held_area_busy', line.n, { count: line.n });
    case 'held':
      return t('fleet.top.tiles.held_reason_line', {
        count: line.n,
        label: t(HELD_REASON_KEY[line.reason]),
      });
    case 'held_unclassified':
      return t('fleet.top.tiles.held_unclassified', { count: line.n });
  }
}

const machines = (count: number): string => tPlural('fleet.top.tiles.machines', count, { count });

const of = (count: number, total: number): string => t('fleet.top.tiles.of', { count, total });

type TileView = { readonly value: string; readonly sub: string; readonly warning?: boolean };

/** The four tiles' value and sub-line, in page order. */
function tileViews(tiles: FleetTiles): readonly TileView[] {
  const { online, busy, queued, attention } = tiles;
  return [
    { value: of(online.online, online.total), sub: onlineSub(online, busy.starting) },
    { value: of(busy.working, busy.total), sub: busySub(busy) },
    {
      value: tPlural('fleet.top.tiles.tasks', queued, { count: queued }),
      sub: queueSub(queueLine({ queued, idle: busy.idle, queue: tiles.queue })),
    },
    attention.machines > 0
      ? { value: machines(attention.machines), sub: attentionSub(attention.parts), warning: true }
      : { value: t('fleet.top.tiles.all_clear'), sub: t('fleet.top.tiles.all_clear_sub') },
  ];
}

/** Each tile's label and the Machines filter it applies (`null`: it filters nothing). */
const TILES: readonly { readonly label: string; readonly filter: MachineFilter | null }[] = [
  { label: 'fleet.top.tiles.online', filter: 'all' },
  { label: 'fleet.top.tiles.busy', filter: 'working' },
  { label: 'fleet.top.tiles.queue', filter: null },
  { label: 'fleet.top.tiles.attention', filter: 'attention' },
];

export function FleetHealthTiles(props: {
  /** The counts, or `undefined` while the utilization read is in flight or failed. */
  readonly tiles: FleetTiles | undefined;
  readonly error: boolean;
  /** The Machines list's filter now: its tile reads pressed. */
  readonly filter: MachineFilter;
  readonly onFilter: (filter: MachineFilter) => void;
}): JSX.Element {
  const views = (): readonly TileView[] | undefined => props.tiles && tileViews(props.tiles);
  return (
    <ul class="fleet-tiles" aria-label={t('fleet.top.tiles.aria')}>
      <For each={TILES}>
        {(tile, i) => {
          const body = (): JSX.Element => (
            <>
              <span class="fleet-tile__label">{t(tile.label)}</span>
              <StatValue value={views()?.[i()]?.value} error={props.error} />
              <span class="fleet-tile__sub">{views()?.[i()]?.sub ?? ''}</span>
            </>
          );
          const tone = (): string | undefined =>
            views()?.[i()]?.warning === true ? 'warning' : undefined;
          return (
            <li>
              <Show
                when={tile.filter}
                fallback={
                  <div class="fleet-tile" data-tone={tone()}>
                    {body()}
                  </div>
                }
              >
                {(filter) => (
                  <button
                    type="button"
                    class="fleet-tile fleet-tile--filter"
                    data-tone={tone()}
                    aria-pressed={props.filter === filter()}
                    onClick={() => props.onFilter(filter())}
                  >
                    {body()}
                  </button>
                )}
              </Show>
            </li>
          );
        }}
      </For>
    </ul>
  );
}
